import React, { useCallback, useEffect, useState } from "react";

import webhookService from "./services/webhookService";

import WebhookLoading from "./components/WebhookLoading";
import WebhookError from "./components/WebhookError";
import WebhookEmpty from "./components/WebhookEmpty";
import WebhookHeader from "./components/WebhookHeader";
import WebhookField from "./components/WebhookField";
import WebhookActivity from "./components/WebhookActivity";
import WebhookInfoNotice from "./components/WebhookInfoNotice";
import WebhookFooter from "./components/WebhookFooter";
import { normalizeWebhookCallbackUrl } from "./utils/webhookUrl";

function Webhooks() {
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState(null);
  const [canManageConnection, setCanManageConnection] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);


  const loadWebhook = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);
      setCreateError(null);

      const whatsappSetup = JSON.parse(localStorage.getItem("whatsapp_setup") || "null");
      const phoneNumberId = whatsappSetup?.phoneNumberId;
      setPhoneNumberId(phoneNumberId || null);

      if (!phoneNumberId) {
        throw new Error(
          "WhatsApp phone number is not configured."
        );
      }

      const response = await webhookService.show(phoneNumberId);
      const webhookData = response?.data?.data;

      setWebhook(webhookData || null);
      setCanManageConnection(response?.data?.meta?.can_manage_connection === true);
    } catch (error) {
      setWebhook(null);
      setCanManageConnection(false);
      console.error("Failed to load webhook:", error);
      setError(error?.response?.data?.message || error?.message || "Unable to load webhook configuration.");
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const createWebhook = async () => {
    if (!phoneNumberId || !canManageConnection || creating) return;

    setCreating(true);
    setCreateError(null);
    try {
      const response = await webhookService.create(phoneNumberId);
      const webhookData = response?.data?.data;
      if (!webhookData?.id) {
        throw new Error("The server did not return a webhook configuration. Please try again.");
      }
      setWebhook(webhookData);
    } catch (error) {
      setCreateError(error?.response?.data?.message || error?.message || "Unable to create webhook configuration.");
    } finally {
      setCreating(false);
    }
  };

  
  useEffect(() => {
    loadWebhook();
  }, [loadWebhook]);

  
  const copyToClipboard = async (value, type) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      setTimeout(() => {
        setCopied("");
      }, 1500);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const formatDate = (date) => {
    if (!date) return "Never";
  
    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "—";
    
    return parsedDate.toLocaleString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) return <WebhookLoading />;
  if (error) return (<WebhookError error={error} onRetry={() => loadWebhook()}/>);
  if (!webhook) return (
    <WebhookEmpty
      canManageConnection={canManageConnection}
      onCreate={createWebhook}
      creating={creating}
      error={createError}
      onRefresh={() => loadWebhook()}
    />
  );
  
  const isVerified =  webhook?.status?.toLowerCase() === "verified";
  const callbackUrl = normalizeWebhookCallbackUrl(webhook.callback_url);

  return (
    <div className="min-w-0">
      <div className="overflow-hidden rounded-[22px] border border-[#d8e7ed] bg-white shadow-[0_16px_44px_rgba(15,43,66,0.06)] dark:border-dark-border dark:bg-dark-card">

          {/* Header */}
          <WebhookHeader
            refreshing={refreshing}
            onRefresh={() => loadWebhook(true)}
            isVerified={isVerified}
          />

          <div className="space-y-5 p-4 sm:p-5 lg:p-6">

            {/* Callback URL */}
            <WebhookField
              label="Callback URL"
              description="Meta uses this endpoint to deliver WhatsApp webhook events."
              hint="Public HTTPS endpoint"
              value={callbackUrl}
              type="text"
              copied={copied === "callback"}
              onCopy={() =>
                copyToClipboard(
                  callbackUrl,
                  "callback"
                )
              }
            />

            {/* Verify Token */}
            <WebhookField
              label="Verify Token"
              description="Use this token when verifying the webhook with Meta."
              hint="Meta verification credential"
              value={webhook.verify_token}
              type="password"
              copied={copied === "token"}
              onCopy={() =>
                copyToClipboard(
                  webhook.verify_token,
                  "token"
                )
              }
            />

            <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_310px]">
              <WebhookActivity
                webhook={webhook}
                formatDate={formatDate}
              />

              <WebhookInfoNotice />
            </div>
          </div>

          {/* Footer */}
          <WebhookFooter
            isVerified={isVerified}
            updatedAt={formatDate(webhook.updated_at)}
          />
      </div>
    </div>
  );
}

export default Webhooks;
